import { useMemo, useCallback } from 'react';
import { api } from '../../../core/api/queries';
import { useReportPage } from '../../../shared/hooks';
import { ReportPageLayout } from '../../../shared/components/layouts';
import { useNotificationsMutations } from '../hooks/use-notifications-mutations';
import { columnBuilder } from '../../../shared/utils';
import { renderTimestamp } from '../../../shared/components/tables';
import type { NotificationReportDto } from '../api/notifications.api';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';

/**
 * Notifications Report Page
 *
 * Displays sent/received notifications with filtering and sorting.
 * Role-based columns: DBEST users see additional ID columns.
 *
 * Features:
 * - 14 columns (11 common + 3 DBEST-only)
 * - Button to view notification content
 * - Server-side filtering on text fields
 * - Boolean filter for read status (DA/NU)
 * - Excel export
 *
 * Architecture (Refactored 2025-11-01):
 * - Uses useReportPage hook (consolidates useState, useQuery, useSpringReport, useReportExport)
 * - Uses ReportPageLayout component (consolidates ReportControls + DataTable + SpringPagination JSX)
 * - Uses columnBuilder for column definitions
 * - Uses separate mutations hook (useNotificationsMutations)
 * - Reduced from 171 lines to 100 lines (42% reduction)
 */
export function NotificationsReportPage() {
    // ==========================================
    // 1. DATA FETCHING & REPORT INTERFACE
    // ==========================================

    const { report, adapters, handleExport } = useReportPage({
        queryHook: api.notifications.report,
        initialSort: ['notification.sendAt,desc'], // Latest notifications first
        exportEndpoint: '/api/notifications/report',
        exportFilename: 'raport_notificari'
    });

    // ==========================================
    // 2. PAGE-SPECIFIC HOOKS
    // ==========================================

    const mutations = useNotificationsMutations();
    const { userInfo } = useAuthProviderV2();
    const { ROLES } = useRoutesProvider();

    const isDbest = userInfo?.role === ROLES.DBEST;

    // ==========================================
    // 3. ACTION HANDLERS
    // ==========================================

    const handleMarkAsRead = useCallback(async (notificationId: string) => {
        await mutations.markAsRead.mutateAsync(notificationId);
        // Mutations hook handles cache invalidation automatically
    }, [mutations]);

    // ==========================================
    // 4. COLUMN DEFINITIONS
    // ==========================================

    const columns = useMemo(() =>
        columnBuilder<NotificationReportDto>()
            // DBEST-only ID columns
            .when(isDbest, (b) => b
                .text('id', 'Id', undefined, { filterable: false })
                .nested('notification.id', 'Id notificare', (row) => row.notification?.id, { filterable: false })
                .nested('receiver.id', 'Id destinatar', (row) => row.receiver?.id, { filterable: false })
            )
            // Action column
            .action('notification.content', 'Conținut notificare', (_value, row) => (
                <Button
                    onClick={() => row.notification?.id && handleMarkAsRead(row.notification.id)}
                    className="px-0 pt-[10px] font-semibold disabled:cursor-not-allowed"
                    variant="link"
                >
                    Deschide notificarea
                </Button>
            ))
            // Timestamp columns
            .custom({
                key: 'notification.sendAt',
                label: 'Notificare trimisă la data',
                sortable: true,
                filterable: false,
                // Calculul de dinainte presupunea secunde Unix: pentru un Date impartea la
                // 1000, iar pentru orice altceva inmultea direct cu 1000. Cum transformerele
                // generate nu ruleaza (vezi docs/knowledge), `sendAt` ajunge string ISO, deci
                // `"2025-10-26T..." * 1000` = NaN si coloana afisa "Invalid Date".
                // renderTimestamp trece prin formatUnixTimestamp, care trateaza toate formele.
                render: (_, row) => renderTimestamp(row.notification?.sendAt)
            })
            .nested('receiver.role', 'Rol destinatar', (row) => row.receiver?.role, { filterable: false })
            .timestamp('readAt', 'Notificare citită la data')
            .boolean('isRead', 'Notificare citită', 'DA/NU')
            // Text columns
            .nested('notification.subject', 'Subiect notificare', (row) => row.notification?.subject, { filterPlaceholder: 'Subiect...' })
            .nested('receiver.nume', 'Destinatar', (row) => row.receiver?.nume, { filterPlaceholder: 'Destinatar...' })
            .nested('receiver.email', 'Email destinatar', (row) => row.receiver?.email, { filterPlaceholder: 'Email...' })
            .nested('receiver.telefon', 'Telefon destinatar', (row) => row.receiver?.telefon, { filterPlaceholder: 'Telefon...' })
            .nested('receiver.school.codSe', 'Cod SE', (row) => row.receiver?.school?.codSe, { filterPlaceholder: 'Cod SE...' })
            .nested('receiver.school.numeInstitutieInvatamant', 'Denumire instituție', (row) => row.receiver?.school?.numeInstitutieInvatamant, { filterPlaceholder: 'Denumire...' })
            .build(),
        [isDbest, handleMarkAsRead]
    );

    // ==========================================
    // 5. RENDER
    // ==========================================

    return (
        <ReportPageLayout
            title="Raport Notificări"
            report={report}
            adapters={adapters}
            handleExport={handleExport}
            columns={columns}
        />
    );
}
